import { n as resolveLocalPath, t as resolveGlobalPath } from "./paths-Clz-xMIL.js";
import { resolve, sep } from "node:path";
import * as clack from "@clack/prompts";
import { attempt } from "@kidd-cli/utils/fp";
import { existsSync, lstatSync, mkdirSync, readFileSync, unlinkSync, writeFileSync } from "node:fs";
import pc from "picocolors";
import { jsonParse, jsonStringify } from "@kidd-cli/utils/json";
import { TAG, hasTag, withTag } from "@kidd-cli/utils/tag";
//#region src/lib/dotdir/create-dot-directory-client.ts
/**
* Create a scoped {@link DotDirectoryClient} for a single resolved directory.
*
* All filesystem operations are synchronous (consistent with {@link FileStore}).
* Protected files are checked against the shared {@link ProtectionRegistry}.
* Filenames are validated to prevent path traversal outside the scoped directory.
*
* @param options - Directory path, location scope, and protection registry.
* @returns A frozen DotDirectoryClient instance.
*/
function createDotDirectoryClient(options) {
	const { dir, location, registry } = options;
	const resolvedDir = resolve(dir);
	/**
	* Resolve a filename and validate it stays within the scoped directory.
	*
	* @private
	* @param filename - The filename to resolve.
	* @returns A Result with the resolved path on success, or a path_traversal error.
	*/
	function safePath(filename) {
		const resolved = resolve(resolvedDir, filename);
		if (!resolved.startsWith(resolvedDir + sep) && resolved !== resolvedDir) return [{
			message: `Path "${filename}" escapes the dot directory boundary.`,
			type: "path_traversal"
		}, null];
		const [, stat] = attempt(() => lstatSync(resolved));
		if (stat && stat.isSymbolicLink()) return [{
			message: `Path "${filename}" is a symbolic link, which is not allowed.`,
			type: "path_traversal"
		}, null];
		return [null, resolved];
	}
	/**
	* Check whether a file is protected and access was not explicitly opted-in.
	*
	* @private
	* @param filename - The filename to check.
	* @param accessOptions - Access options that may include the bypass flag.
	* @returns A DotDirectoryError if the file is protected, or null.
	*/
	function checkProtection(filename, accessOptions) {
		if (accessOptions !== void 0 && accessOptions.dangerouslyAccessProtectedFile === true) return null;
		if (registry.has(location, filename)) return {
			message: `File "${filename}" is protected in ${location} scope. Pass { dangerouslyAccessProtectedFile: true } to access it.`,
			type: "protected_file"
		};
		return null;
	}
	/**
	* Ensure the directory exists, creating it with mode 0o700 if needed.
	*
	* @private
	* @returns A Result with the directory path on success.
	*/
	function ensure() {
		const [error] = attempt(() => {
			mkdirSync(resolvedDir, {
				mode: 448,
				recursive: true
			});
		});
		if (error) return [{
			message: `Failed to create directory "${resolvedDir}": ${error.message}`,
			type: "fs_error"
		}, null];
		return [null, resolvedDir];
	}
	/**
	* Read a file as a raw string.
	*
	* @private
	* @param filename - The filename to read.
	* @param accessOptions - Access options.
	* @returns A Result with the file content on success.
	*/
	function read(filename, accessOptions) {
		const protectionError = checkProtection(filename, accessOptions);
		if (protectionError) return [protectionError, null];
		const [pathError, filePath] = safePath(filename);
		if (pathError) return [pathError, null];
		const [error, content] = attempt(() => readFileSync(filePath, "utf8"));
		if (error) return [{
			message: `Failed to read "${filePath}": ${error.message}`,
			type: "fs_error"
		}, null];
		return [null, content];
	}
	/**
	* Write a raw string to a file (mode 0o600). Creates the directory if needed.
	*
	* @private
	* @param filename - The filename to write.
	* @param content - The string content.
	* @param writeOptions - Write options.
	* @returns A Result with the written file path on success.
	*/
	function write(filename, content, writeOptions) {
		const protectionError = checkProtection(filename, writeOptions);
		if (protectionError) return [protectionError, null];
		const [pathError, filePath] = safePath(filename);
		if (pathError) return [pathError, null];
		const [ensureError] = ensure();
		if (ensureError) return [ensureError, null];
		const [error] = attempt(() => {
			writeFileSync(filePath, content, {
				encoding: "utf8",
				mode: 384
			});
		});
		if (error) return [{
			message: `Failed to write "${filePath}": ${error.message}`,
			type: "fs_error"
		}, null];
		return [null, filePath];
	}
	/**
	* Read and parse a JSON file, optionally validating with a Zod schema.
	*
	* @private
	* @param filename - The filename to read.
	* @param readOptions - Read options with optional schema.
	* @returns A Result with the parsed (and optionally validated) data.
	*/
	function readJson(filename, readOptions) {
		const [readError, content] = read(filename, readOptions);
		if (readError) return [readError, null];
		const [parseError, parsed] = jsonParse(content);
		if (parseError) return [{
			message: `Failed to parse "${filename}": ${parseError.message}`,
			type: "parse_error"
		}, null];
		if (readOptions !== void 0 && readOptions.schema !== void 0) {
			const result = readOptions.schema.safeParse(parsed);
			if (!result.success) return [{
				message: `Validation failed for "${filename}": ${result.error.message}`,
				type: "parse_error"
			}, null];
			return [null, result.data];
		}
		return [null, parsed];
	}
	/**
	* Serialize data to JSON and write it to a file.
	*
	* @private
	* @param filename - The filename to write.
	* @param data - The data to serialize.
	* @param writeOptions - Write options.
	* @returns A Result with the written file path on success.
	*/
	function writeJson(filename, data, writeOptions) {
		const [stringifyError, json] = jsonStringify(data, { pretty: true });
		if (stringifyError) return [{
			message: `Failed to serialize "${filename}": ${stringifyError.message}`,
			type: "parse_error"
		}, null];
		return write(filename, json, writeOptions);
	}
	/**
	* Check whether a file exists in the directory.
	*
	* @private
	* @param filename - The filename to check.
	* @returns True if the file exists and the path is within the directory boundary.
	*/
	function fileExists(filename) {
		const [pathError, filePath] = safePath(filename);
		if (pathError) return false;
		return existsSync(filePath);
	}
	/**
	* Remove a file from the directory. Idempotent — succeeds if the file does not exist.
	*
	* @private
	* @param filename - The filename to remove.
	* @param accessOptions - Access options.
	* @returns A Result with the file path on success.
	*/
	function remove(filename, accessOptions) {
		const protectionError = checkProtection(filename, accessOptions);
		if (protectionError) return [protectionError, null];
		const [pathError, filePath] = safePath(filename);
		if (pathError) return [pathError, null];
		if (!existsSync(filePath)) return [null, filePath];
		const [error] = attempt(() => {
			unlinkSync(filePath);
		});
		if (error) return [{
			message: `Failed to remove "${filePath}": ${error.message}`,
			type: "fs_error"
		}, null];
		return [null, filePath];
	}
	/**
	* Resolve the full absolute path for a filename within the directory.
	*
	* Validates the filename against path traversal and symlink checks
	* via {@link safePath}.
	*
	* @private
	* @param filename - The filename to resolve.
	* @returns A Result with the absolute path on success, or a path_traversal error.
	*/
	function resolvePath(filename) {
		return safePath(filename);
	}
	return Object.freeze({
		dir: resolvedDir,
		ensure,
		exists: fileExists,
		path: resolvePath,
		read,
		readJson,
		remove,
		write,
		writeJson
	});
}
//#endregion
//#region src/lib/dotdir/protection.ts
/**
* Create a {@link ProtectionRegistry} backed by a `Set<string>`.
*
* Keys are serialized as `"location:filename"`. The registry is shared
* by reference across all `DotDirectoryClient` instances from the same `DotDirectory`.
*
* @returns A frozen ProtectionRegistry instance.
*/
function createProtectionRegistry() {
	const entries = /* @__PURE__ */ new Set();
	return Object.freeze({
		add(entry) {
			entries.add(toKey(entry.location, entry.filename));
		},
		has(location, filename) {
			return entries.has(toKey(location, filename));
		}
	});
}
/**
* Serialize a location and filename into a registry key.
*
* @private
* @param location - The directory location.
* @param filename - The filename.
* @returns A colon-delimited key string.
*/
function toKey(location, filename) {
	return `${location}:${filename}`;
}
//#endregion
//#region src/lib/dotdir/create-dot-directory.ts
/**
* Create a {@link DotDirectory} that returns scoped {@link DotDirectoryClient}
* handles and manages a shared protection registry.
*
* @param options - The resolved directory names from `ctx.meta.dirs`.
* @returns A frozen DotDirectory instance.
*/
function createDotDirectory(options) {
	const { dirs } = options;
	const registry = createProtectionRegistry();
	/**
	* Get a DotDirectoryClient scoped to the global home directory.
	*
	* @private
	* @returns A DotDirectoryClient for the global scope.
	*/
	function global() {
		return createDotDirectoryClient({
			dir: resolveGlobalPath({ dirName: dirs.global }),
			location: "global",
			registry
		});
	}
	/**
	* Get a DotDirectoryClient scoped to the project-local directory.
	*
	* @private
	* @returns A Result with a DotDirectoryClient on success, or a no_project_root error.
	*/
	function local() {
		const dir = resolveLocalPath({ dirName: dirs.local });
		if (dir === null) return [{
			message: "No project root found",
			type: "no_project_root"
		}, null];
		return [null, createDotDirectoryClient({
			dir,
			location: "local",
			registry
		})];
	}
	/**
	* Register a file as protected in the shared registry.
	*
	* @private
	* @param entry - The file entry to protect.
	*/
	function protect(entry) {
		registry.add(entry);
	}
	return Object.freeze({
		global,
		local,
		protect
	});
}
//#endregion
//#region src/context/resolve-defaults.ts
/**
* Shared empty base object. Returned when no defaults are configured to avoid
* allocating a new `{}` on every call.
*/
const EMPTY_CLACK_BASE = Object.freeze({});
/**
* Map display-config-style defaults (`guide`, `input`, `output`) to clack's
* expected option names (`withGuide`, `input`, `output`).
*
* Returns {@link EMPTY_CLACK_BASE} when `defaults` is undefined so callers
* can spread without allocating a throwaway object.
*
* @param defaults - Per-call defaults from display config, if any.
* @returns A plain object suitable for spreading into clack calls.
*/
function resolveClackBase(defaults) {
	if (defaults === void 0) return EMPTY_CLACK_BASE;
	return {
		withGuide: defaults.guide,
		input: defaults.input,
		output: defaults.output
	};
}
/**
* Merge clack base defaults with per-call options, skipping the spread when
* `opts` is undefined.
*
* @param base - The resolved clack base defaults.
* @param opts - Per-call options from the method caller.
* @returns The merged options object, or `base` if `opts` is undefined.
*/
function mergeClackOpts(base, opts) {
	if (opts === void 0) return base;
	return {
		...base,
		...opts
	};
}
//#endregion
//#region src/lib/log.ts
/**
* Create a {@link Log} instance that delegates to `@clack/prompts` for
* structured terminal output.
*
* When `defaults` are provided, they are spread as the base of every clack
* call. Method-level options always take precedence.
*
* @param options - Optional configuration for the log instance.
* @returns A frozen Log object.
*/
function createLog(options) {
	const output = resolveOutput(options);
	const base = resolveClackBase(options?.defaults);
	const boxBase = resolveBoxBase(base, options?.boxDefaults);
	return Object.freeze({
		error(message, opts) {
			clack.log.error(message, mergeClackOpts(base, opts));
		},
		info(message, opts) {
			clack.log.info(message, mergeClackOpts(base, opts));
		},
		intro(title) {
			clack.intro(title, base);
		},
		message(message, opts) {
			clack.log.message(message, mergeClackOpts(base, opts));
		},
		newline() {
			output.write("\n");
		},
		note(message, title, opts) {
			clack.note(message, title, mergeClackOpts(base, opts));
		},
		box(message, title, opts) {
			clack.box(message, title, mergeClackOpts(boxBase, opts));
		},
		outro(message) {
			clack.outro(message, base);
		},
		raw(text) {
			output.write(`${text}\n`);
		},
		step(message, opts) {
			clack.log.step(message, mergeClackOpts(base, opts));
		},
		success(message, opts) {
			clack.log.success(message, mergeClackOpts(base, opts));
		},
		warn(message, opts) {
			clack.log.warn(message, mergeClackOpts(base, opts));
		},
		stream: createStreamLog()
	});
}
/**
* Resolve the output stream from options, defaulting to `process.stderr`.
*
* @private
* @param options - Optional configuration containing an output stream.
* @returns The resolved writable stream.
*/
function resolveOutput(options) {
	if (options !== void 0 && options.output !== void 0) return options.output;
	return process.stderr;
}
/**
* Resolve the base options for box calls by merging common defaults with box-specific defaults.
*
* @private
* @param base - Common clack defaults (withGuide, output).
* @param boxDefaults - Box-specific display config defaults.
* @returns A merged object suitable for spreading into `clack.box()` calls.
*/
function resolveBoxBase(base, boxDefaults) {
	if (boxDefaults === void 0) return base;
	return {
		...base,
		...boxDefaults
	};
}
/**
* Create the streaming log methods that delegate to `@clack/prompts` stream API.
*
* Note: clack's stream methods (except `message`) do not accept per-call options,
* so display defaults are not passed here.
*
* @private
* @returns A frozen StreamLog object.
*/
function createStreamLog() {
	return Object.freeze({
		async info(iterable) {
			await clack.stream.info(iterable);
		},
		async success(iterable) {
			await clack.stream.success(iterable);
		},
		async error(iterable) {
			await clack.stream.error(iterable);
		},
		async warn(iterable) {
			await clack.stream.warn(iterable);
		},
		async step(iterable) {
			await clack.stream.step(iterable);
		},
		async message(iterable) {
			await clack.stream.message(iterable);
		}
	});
}
/**
* Create a ContextError with an exit code and optional error code.
*
* Used to surface user-facing CLI errors with clean messages.
* The error carries a Symbol-based tag for reliable type-narrowing
* via {@link isContextError}.
*
* @param message - Human-readable error message.
* @param options - Optional error code and exit code overrides.
* @returns A ContextError instance.
*/
function createContextError(message, options) {
	const data = createContextErrorData(message, options);
	const error = new Error(data.message);
	error.name = "ContextError";
	Object.defineProperty(error, TAG, {
		enumerable: false,
		value: "ContextError",
		writable: false
	});
	Object.defineProperty(error, "code", {
		enumerable: true,
		value: data.code,
		writable: false
	});
	Object.defineProperty(error, "exitCode", {
		enumerable: true,
		value: data.exitCode,
		writable: false
	});
	return error;
}
/**
* Type guard that narrows an unknown value to {@link ContextError}.
*
* Checks that the value is an Error instance whose `[TAG]` property
* equals `'ContextError'`, which distinguishes CLI-layer errors from
* unexpected exceptions.
*
* @param error - The value to check.
* @returns `true` when the value is a ContextError.
*/
function isContextError(error) {
	if (error instanceof Error) return hasTag(error, "ContextError");
	return false;
}
function resolveExitCode(options) {
	if (options && options.exitCode !== void 0) return options.exitCode;
	return 1;
}
function resolveCode(options) {
	if (options && options.code !== void 0) return options.code;
}
function createContextErrorData(message, options) {
	return withTag({
		code: resolveCode(options),
		exitCode: resolveExitCode(options),
		message
	}, "ContextError");
}
//#endregion
//#region src/context/format.ts
/**
* Create the pure string formatter methods for a context.
*
* @private
* @returns A Format instance with json and table formatters.
*/
function createContextFormat() {
	return Object.freeze({
		json(data) {
			const [, json] = jsonStringify(data, { pretty: true });
			return `${json}\n`;
		},
		table(rows) {
			if (rows.length === 0) return "";
			const [firstRow] = rows;
			if (!firstRow) return "";
			return formatTable(rows, Object.keys(firstRow));
		}
	});
}
/**
* Format an unknown value as a string for table cell display.
*
* @private
* @param val - The value to format.
* @returns The stringified value, or empty string for undefined.
*/
function formatStringValue(val) {
	if (val === void 0) return "";
	return String(val);
}
/**
* Create a padded header row string from column keys and widths.
*
* @private
* @param options - The keys and column widths.
* @returns A formatted header string.
*/
function createTableHeader(options) {
	const { keys, widths } = options;
	return keys.map((key, idx) => {
		const width = widths[idx];
		if (width === void 0) return key;
		return key.padEnd(width);
	}).join("  ");
}
/**
* Create a padded row string from a data record, column keys, and widths.
*
* @private
* @param options - The row data, keys, and column widths.
* @returns A formatted row string.
*/
function createTableRow(options) {
	const { row, keys, widths } = options;
	return keys.map((key, idx) => {
		const width = widths[idx];
		const val = formatStringValue(row[key]);
		if (width === void 0) return val;
		return val.padEnd(width);
	}).join("  ");
}
/**
* Compute the maximum column width for each key across all rows.
*
* @private
* @param rows - The data rows.
* @param keys - The column keys.
* @returns An array of column widths.
*/
function computeColumnWidths(rows, keys) {
	return keys.map((key) => {
		const values = rows.map((row) => formatStringValue(row[key]));
		return Math.max(key.length, ...values.map((val) => val.length));
	});
}
/**
* Format a table (header, separator, rows) as a string.
*
* @private
* @param rows - The data rows.
* @param keys - The column keys.
* @returns The formatted table string.
*/
function formatTable(rows, keys) {
	const widths = computeColumnWidths(rows, keys);
	return `${[
		createTableHeader({
			keys,
			widths
		}),
		widths.map((width) => "-".repeat(width)).join("  "),
		...rows.map((row) => createTableRow({
			keys,
			row,
			widths
		}))
	].join("\n")}\n`;
}
//#endregion
//#region src/context/prompts.ts
/**
* Create the interactive prompt methods for a context.
*
* Each method delegates to `@clack/prompts` and unwraps cancel signals
* into a ContextError so the CLI runner can exit cleanly.
*
* When `defaults` are provided, they are spread as the base of every clack
* call. Method-level options always take precedence.
*
* @param options - Optional configuration with per-call defaults.
* @returns A Prompts instance backed by clack.
*/
function createContextPrompts(options) {
	const base = resolveClackBase(options?.defaults);
	return {
		async confirm(opts) {
			return unwrapCancelSignal(await clack.confirm({
				...base,
				...opts
			}));
		},
		async multiselect(opts) {
			return unwrapCancelSignal(await clack.multiselect({
				...base,
				...opts
			}));
		},
		async password(opts) {
			return unwrapCancelSignal(await clack.password({
				...base,
				...opts
			}));
		},
		async select(opts) {
			return unwrapCancelSignal(await clack.select({
				...base,
				...opts
			}));
		},
		async text(opts) {
			return unwrapCancelSignal(await clack.text({
				...base,
				...opts
			}));
		},
		async autocomplete(opts) {
			return unwrapCancelSignal(await clack.autocomplete({
				...base,
				...opts
			}));
		},
		async autocompleteMultiselect(opts) {
			return unwrapCancelSignal(await clack.autocompleteMultiselect({
				...base,
				...opts
			}));
		},
		async groupMultiselect(opts) {
			return unwrapCancelSignal(await clack.groupMultiselect({
				...base,
				...opts
			}));
		},
		async selectKey(opts) {
			return unwrapCancelSignal(await clack.selectKey({
				...base,
				...opts
			}));
		},
		async path(opts) {
			return unwrapCancelSignal(await clack.path({
				...base,
				...opts
			}));
		},
		async group(prompts, opts) {
			return await clack.group(prompts, { onCancel: opts?.onCancel });
		}
	};
}
/**
* Unwrap a prompt result that may be a cancel symbol.
*
* If the user cancelled (Ctrl-C), throws a ContextError. Otherwise returns
* the typed result value.
*
* @private
* @param result - The raw prompt result (value or cancel symbol).
* @returns The unwrapped typed value.
*/
function unwrapCancelSignal(result) {
	if (clack.isCancel(result)) {
		clack.cancel("Operation cancelled.");
		throw createContextError("Prompt cancelled by user", {
			code: "PROMPT_CANCELLED",
			exitCode: 1
		});
	}
	return result;
}
//#endregion
//#region src/context/status.ts
/**
* Create the live status indicator methods for a context.
*
* Provides a pre-created spinner, plus factories for progress bars,
* sequential task runners, and task logs. Each delegates to `@clack/prompts`.
*
* When config defaults are provided, they are merged into each clack call.
* Method-level options always take precedence.
*
* @param options - Optional overrides for status sub-components.
* @returns A frozen Status instance backed by clack.
*/
function createContextStatus(options) {
	const base = resolveClackBase(options?.defaults);
	const spinner = createDefaultSpinner(base, options?.spinnerConfig ?? {});
	const progressConfig = options?.progressConfig ?? {};
	return Object.freeze({
		spinner,
		progress(opts) {
			const bar = clack.progress({
				...base,
				...progressConfig,
				...opts
			});
			return {
				start(message) {
					bar.start(message);
				},
				advance(step, message) {
					bar.advance(step, message);
				},
				stop(message) {
					bar.stop(message);
				},
				message(message) {
					bar.message(message);
				},
				cancel(message) {
					bar.cancel(message);
				},
				error(message) {
					bar.error(message);
				},
				clear() {
					bar.clear();
				},
				get isCancelled() {
					return bar.isCancelled;
				}
			};
		},
		async tasks(tasks) {
			await clack.tasks(tasks.map((t) => ({
				title: t.title,
				task: t.task,
				enabled: t.enabled
			})), base);
		},
		taskLog(opts) {
			const tl = clack.taskLog({
				...base,
				...opts
			});
			return {
				message(line, mopts) {
					tl.message(line, mopts);
				},
				success(message, copts) {
					tl.success(message, copts);
				},
				error(message, copts) {
					tl.error(message, copts);
				},
				group(name) {
					const g = tl.group(name);
					return {
						message(line, mopts) {
							g.message(line, mopts);
						},
						success(message) {
							g.success(message);
						},
						error(message) {
							g.error(message);
						}
					};
				}
			};
		}
	});
}
/**
* Create the default clack spinner with merged config.
*
* @private
* @param base - Common clack defaults (withGuide, output).
* @param config - Spinner-specific display config.
* @returns A Spinner instance.
*/
function createDefaultSpinner(base, config) {
	return clack.spinner({
		...base,
		...config,
		frames: resolveFrames(config.frames)
	});
}
/**
* Convert readonly frames tuple to a mutable array for clack, or undefined if not set.
*
* @private
* @param frames - The readonly frames from display config.
* @returns A mutable string array or undefined.
*/
function resolveFrames(frames) {
	if (frames === void 0) return;
	return [...frames];
}
//#endregion
//#region src/context/store.ts
/**
* Create an in-memory key-value store.
*
* @private
* @returns A Store instance backed by a Map.
*/
function createMemoryStore() {
	const map = /* @__PURE__ */ new Map();
	return {
		clear() {
			map.clear();
		},
		delete(key) {
			return map.delete(key);
		},
		get(key) {
			return map.get(key);
		},
		has(key) {
			return map.has(key);
		},
		set(key, value) {
			map.set(key, value);
		}
	};
}
//#endregion
//#region src/context/create-context.ts
/**
* Create the {@link CommandContext} object threaded through middleware and command handlers.
*
* Assembles log, status, format, store, prompts, and meta from
* the provided options into a single immutable context. Each sub-system is
* constructed via its own factory so this function remains a lean orchestrator.
*
* When `display` is provided, per-call defaults are extracted and passed
* to each factory. Global-only settings (`aliases`, `messages`) are applied
* via `updateSettings()` at boot time in `cli()`, not here.
*
* @param options - Args, config, and meta for the current invocation.
* @returns A fully constructed CommandContext.
*/
function createContext(options) {
	const dc = options.display ?? {};
	const commonDefaults = resolveCommonDefaults(dc);
	const ctxLog = options.log ?? createLog({
		boxDefaults: dc.box,
		defaults: commonDefaults,
		output: dc.output
	});
	const ctxStatus = resolveStatus(options, dc, commonDefaults);
	const ctxFormat = createContextFormat();
	const ctxStore = createMemoryStore();
	const ctxPrompts = options.prompts ?? createContextPrompts({ defaults: commonDefaults });
	const ctxMeta = {
		command: options.meta.command,
		dirs: Object.freeze({ ...options.meta.dirs }),
		name: options.meta.name,
		version: options.meta.version
	};
	const ctxDotdir = createDotDirectory({ dirs: options.meta.dirs });
	return {
		args: options.args,
		colors: Object.freeze({ ...pc }),
		dotdir: ctxDotdir,
		fail(message, failOptions) {
			throw createContextError(message, failOptions);
		},
		format: ctxFormat,
		log: ctxLog,
		meta: ctxMeta,
		prompts: ctxPrompts,
		raw: Object.freeze({ argv: Object.freeze([...options.argv]) }),
		status: ctxStatus,
		store: ctxStore
	};
}
/**
* Extract the common per-call defaults from display config.
*
* @private
* @param dc - The display config, if any.
* @returns Common defaults suitable for prompts, log, and status factories.
*/
function resolveCommonDefaults(dc) {
	return {
		guide: dc.guide,
		input: dc.input,
		output: dc.output
	};
}
/**
* Resolve the Status instance from options or create a default one.
*
* @private
* @param options - The create context options.
* @param dc - The resolved display config.
* @param commonDefaults - Common per-call defaults from display config.
* @returns A Status instance.
*/
function resolveStatus(options, dc, commonDefaults) {
	if (options.status !== void 0) return options.status;
	return createContextStatus({
		defaults: commonDefaults,
		progressConfig: dc.progress,
		spinnerConfig: dc.spinner
	});
}
//#endregion
export { isContextError as n, createLog as r, createContext as t };

//# sourceMappingURL=create-context-BhqTipOB.js.map